import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { postsAPI, clientsAPI, packagesAPI, uploadAPI, aiAPI } from '../../services/api';
import { Card, Button } from '../ui';
import Loading from '../common/Loading';
import { handleApiError, getImageUrl, showToast } from '../../utils/helpers';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const ArticleEditorPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;
  const { user } = useAuth();
  const { isRTL } = useLanguage();
  const quillRef = useRef(null);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [generatingAIContent, setGeneratingAIContent] = useState(false);
  const [generatingAISEO, setGeneratingAISEO] = useState(false);
  const [hasAIGeneration, setHasAIGeneration] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientPackages, setClientPackages] = useState([]);
  const [formData, setFormData] = useState({
    client_id: '',
    title: '',
    rich_description: '',
    meta_title: '',
    meta_description: '',
    seo_keywords: [],
    article_details: { author: '', category: '', tags: '' },
    assigned_writer_id: user?.id || ''
  });

  useEffect(() => {
    if (isNew) {
      fetchClients();
    } else {
      fetchArticle();
    }
  }, [id, isNew]);

  useEffect(() => {
    const checkAI = async () => {
      try {
        const res = await aiAPI.checkAvailable();
        setHasAIGeneration(res.data?.hasAIGeneration ?? false);
      } catch {
        setHasAIGeneration(false);
      }
    };
    checkAI();
  }, []);

  useEffect(() => {
    if (formData.client_id) {
      fetchClientPackages();
    } else {
      setClientPackages([]);
    }
  }, [formData.client_id]);

  const fetchClients = async () => {
    try {
      const res = await clientsAPI.getAll();
      setClients(res.data.clients || []);
    } catch (err) {
      showToast.error(handleApiError(err));
    }
  };

  const fetchClientPackages = async () => {
    try {
      const res = await packagesAPI.getAvailablePackages(formData.client_id);
      setClientPackages(res.data.available_packages || []);
    } catch {
      setClientPackages([]);
    }
  };

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const res = await postsAPI.getById(id);
      const p = res.data.post;
      if (p.content_type !== 'blog') {
        showToast.error(isRTL ? 'هذا المنشور ليس مقالة' : 'This post is not an article');
        navigate('/articles');
        return;
      }
      setFormData({
        client_id: p.client_id,
        title: p.title,
        rich_description: p.rich_description || '',
        meta_title: p.meta_title || '',
        meta_description: p.meta_description || '',
        seo_keywords: p.seo_keywords || [],
        article_details: p.article_details || { author: '', category: '', tags: '' },
        assigned_writer_id: p.assigned_writer_id || ''
      });
    } catch (err) {
      showToast.error(handleApiError(err));
      navigate('/articles');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArticleDetailChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      article_details: { ...prev.article_details, [field]: value }
    }));
  };

  const handleImageUpload = useCallback(async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        showToast.error(isRTL ? 'حجم الصورة كبير جداً (الحد الأقصى 5MB)' : 'Image too large (max 5MB)');
        return;
      }
      try {
        const response = await uploadAPI.uploadImage(file);
        const imageUrl = getImageUrl(response.data.url || response.data.imageUrl);
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          if (range) {
            quill.insertEmbed(range.index, 'image', imageUrl);
            quill.setSelection(range.index + 1);
          }
        }
      } catch (err) {
        showToast.error(handleApiError(err));
      }
    };
  }, [isRTL]);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'color': [] }, { 'background': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: { image: handleImageUpload }
    }
  }), [handleImageUpload]);

  const formats = useMemo(() => [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'color', 'background', 'link', 'image'
  ], []);

  const handleGenerateAIContent = async () => {
    if (!hasAIGeneration) {
      showToast.error(isRTL
        ? 'ميزة AI غير متاحة في باقاتك الحالية. يرجى الترقية للوصول لهذه الميزة.'
        : 'AI feature not available in your current plan. Please upgrade to access this feature.');
      return;
    }
    if (isNew) {
      if (!formData.client_id || !formData.title?.trim()) {
        showToast.error(isRTL ? 'العميل والعنوان مطلوبان للتوليد' : 'Client and title required for generation');
        return;
      }
      try {
        setGeneratingAIContent(true);
        const res = await aiAPI.generateArticleDraft({
          client_id: formData.client_id,
          title: formData.title.trim(),
          description: formData.rich_description?.replace(/<[^>]*>/g, '').slice(0, 500) || null,
          article_details: formData.article_details,
          language: isRTL ? 'arabic' : 'english',
          length: 'medium',
          style: 'blog'
        });
        handleChange('rich_description', res.data.content || '');
        showToast.success(isRTL ? 'تم توليد المحتوى بنجاح!' : 'Content generated successfully!');
      } catch (err) {
        showToast.error(handleApiError(err));
      } finally {
        setGeneratingAIContent(false);
      }
    } else {
      try {
        setGeneratingAIContent(true);
        const res = await aiAPI.generateArticleContent(id, {
          language: isRTL ? 'arabic' : 'english',
          length: 'medium',
          style: 'blog'
        });
        handleChange('rich_description', res.data.content || '');
        showToast.success(isRTL ? 'تم توليد المحتوى بنجاح!' : 'Content generated successfully!');
      } catch (err) {
        showToast.error(handleApiError(err));
      } finally {
        setGeneratingAIContent(false);
      }
    }
  };

  const handleGenerateAISEO = async () => {
    if (!hasAIGeneration) {
      showToast.error(isRTL
        ? 'ميزة AI غير متاحة في باقاتك الحالية.'
        : 'AI feature not available in your current plan.');
      return;
    }
    if (isNew) return;
    try {
      setGeneratingAISEO(true);
      const res = await aiAPI.generateSEO(id, { language: isRTL ? 'arabic' : 'english' });
      if (res.data.meta_title) handleChange('meta_title', res.data.meta_title);
      if (res.data.meta_description) handleChange('meta_description', res.data.meta_description);
      if (res.data.seo_keywords?.length) handleChange('seo_keywords', res.data.seo_keywords);
      showToast.success(isRTL ? 'تم توليد SEO بنجاح!' : 'SEO generated successfully!');
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setGeneratingAISEO(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title?.trim()) {
      showToast.error(isRTL ? 'العنوان مطلوب' : 'Title is required');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        if (!formData.client_id) {
          showToast.error(isRTL ? 'اختر العميل' : 'Select a client');
          setSaving(false);
          return;
        }
        const clientPkg = clientPackages.find(
          p => p.platforms?.includes?.('blog') || p.platform === 'blog' || p.platform === 'website' || !p.platform || p.platform === 'all'
        ) || clientPackages[0];
        const createData = {
          client_id: formData.client_id,
          title: formData.title.trim(),
          description: formData.rich_description?.replace(/<[^>]*>/g, '').slice(0, 200) || null,
          rich_description: formData.rich_description || null,
          meta_title: formData.meta_title || null,
          meta_description: formData.meta_description || null,
          seo_keywords: formData.seo_keywords,
          article_details: formData.article_details,
          platform: 'blog',
          content_type: 'blog',
          specifications: { aspect_ratio: '16:9' },
          assigned_writer_id: formData.assigned_writer_id || user?.id || null,
          client_package_id: clientPkg?.id || null
        };
        const res = await postsAPI.create(createData);
        showToast.success(isRTL ? 'تم إنشاء المقالة' : 'Article created');
        navigate(`/articles/${res.data.post.id}/edit`);
      } else {
        await postsAPI.update(id, {
          title: formData.title.trim(),
          rich_description: formData.rich_description || null,
          meta_title: formData.meta_title || null,
          meta_description: formData.meta_description || null,
          seo_keywords: formData.seo_keywords,
          article_details: formData.article_details,
          assigned_writer_id: formData.assigned_writer_id || null
        });
        showToast.success(isRTL ? 'تم حفظ التعديلات' : 'Changes saved');
      }
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loading fullScreen />;

  return (
    <div className="animate-in fade-in duration-500 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/articles')}
            className="text-slate-600 hover:text-slate-900"
          >
            {isRTL ? '← رجوع' : '← Back'}
          </Button>
          <div className="h-6 w-px bg-slate-200" />
          <h1 className="text-lg font-bold text-slate-900">
            {isNew ? (isRTL ? 'مقالة جديدة' : 'New Article') : (isRTL ? 'تعديل المقالة' : 'Edit Article')}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate('/articles')}>
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            className="bg-gradient-to-r from-sky-600 to-emerald-600 hover:from-sky-700 hover:to-emerald-700 border-0 shadow-md"
          >
            {isRTL ? 'حفظ' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Main content card */}
        <Card className="p-6 sm:p-8 rounded-2xl border-slate-200/80 shadow-sm overflow-hidden">
          {isNew && (
            <div className="mb-6 pb-6 border-b border-slate-100">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                {isRTL ? 'العميل' : 'Client'}
              </label>
              <select
                value={formData.client_id}
                onChange={(e) => handleChange('client_id', e.target.value)}
                className="input w-full max-w-md rounded-xl border-slate-200 focus:ring-sky-500 focus:border-sky-500"
                required
              >
                <option value="">{isRTL ? 'اختر العميل' : 'Select client'}</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>{c.company_name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              {isRTL ? 'عنوان المقالة' : 'Article Title'}
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="input w-full text-lg rounded-xl border-slate-200 focus:ring-sky-500 focus:border-sky-500 py-3"
              placeholder={isRTL ? 'أدخل عنوان المقالة' : 'Enter article title'}
            />
          </div>

          {/* Content Editor */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <label className="text-sm font-semibold text-slate-700">
                {isRTL ? 'المحتوى' : 'Content'}
              </label>
              {hasAIGeneration ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerateAIContent}
                  loading={generatingAIContent}
                  disabled={generatingAIContent || (isNew && (!formData.client_id || !formData.title?.trim()))}
                  className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                >
                  {isRTL ? '✨ توليد المحتوى بـ AI' : '✨ Generate with AI'}
                </Button>
              ) : (
                <span
                  className="text-xs text-slate-400 cursor-not-allowed"
                  title={isRTL ? 'ميزة AI غير متاحة في باقاتك' : 'AI feature not available'}
                >
                  {isRTL ? '✨ توليد بـ AI (غير متاح)' : '✨ Generate with AI (Not Available)'}
                </span>
              )}
            </div>
            <div className="min-h-[320px] rounded-xl border border-slate-200 overflow-hidden bg-white shadow-inner">
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={formData.rich_description}
                onChange={(v) => handleChange('rich_description', v)}
                modules={modules}
                formats={formats}
                placeholder={isRTL ? 'ابدأ الكتابة...' : 'Start typing...'}
                className="article-editor border-0"
                style={{ minHeight: '300px' }}
              />
            </div>
          </div>

          {/* Article Details */}
          <div className="p-5 rounded-xl bg-gradient-to-br from-slate-50 to-sky-50/30 border border-slate-200/80 mb-6">
            <h3 className="text-sm font-semibold text-slate-700 mb-4">
              {isRTL ? 'تفاصيل المقالة' : 'Article Details'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">{isRTL ? 'الكاتب' : 'Author'}</label>
                <input
                  type="text"
                  value={formData.article_details.author || ''}
                  onChange={(e) => handleArticleDetailChange('author', e.target.value)}
                  className="input w-full rounded-lg border-slate-200"
                  placeholder={isRTL ? 'اسم الكاتب' : 'Author name'}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">{isRTL ? 'الفئة' : 'Category'}</label>
                <input
                  type="text"
                  value={formData.article_details.category || ''}
                  onChange={(e) => handleArticleDetailChange('category', e.target.value)}
                  className="input w-full rounded-lg border-slate-200"
                  placeholder={isRTL ? 'مثل: تكنولوجيا' : 'e.g., Technology'}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1.5">{isRTL ? 'العلامات' : 'Tags'}</label>
                <input
                  type="text"
                  value={formData.article_details.tags || ''}
                  onChange={(e) => handleArticleDetailChange('tags', e.target.value)}
                  className="input w-full rounded-lg border-slate-200"
                  placeholder={isRTL ? 'مفصولة بفواصل' : 'Comma separated'}
                />
              </div>
            </div>
          </div>

          {/* SEO Settings */}
          <div className="p-5 rounded-xl bg-gradient-to-br from-sky-50/50 to-emerald-50/30 border border-sky-200/50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <h3 className="text-sm font-semibold text-slate-700">
                {isRTL ? 'إعدادات SEO' : 'SEO Settings'}
              </h3>
              {!isNew && hasAIGeneration && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerateAISEO}
                  loading={generatingAISEO}
                  disabled={generatingAISEO}
                  className="text-sky-600 hover:text-sky-700 hover:bg-sky-50"
                >
                  {isRTL ? '✨ توليد SEO بـ AI' : '✨ Generate SEO with AI'}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">{isRTL ? 'Meta Title' : 'Meta Title'}</label>
                <input
                  type="text"
                  value={formData.meta_title}
                  onChange={(e) => handleChange('meta_title', e.target.value)}
                  className="input w-full rounded-lg border-slate-200"
                  placeholder="50-60 characters"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">{isRTL ? 'Meta Description' : 'Meta Description'}</label>
                <input
                  type="text"
                  value={formData.meta_description}
                  onChange={(e) => handleChange('meta_description', e.target.value)}
                  className="input w-full rounded-lg border-slate-200"
                  placeholder="150-160 characters"
                />
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ArticleEditorPage;
